import { format, parseISO } from 'date-fns';
import React, { useMemo } from 'react';

import { ReactComponent as IconLeave } from '@assets/icons/icon-leave.svg';
import { Calendar, MapMarker } from '@withjoy/joykit/icons';

import { AdminMenu } from '../../AdminMenu';
import {
  AdminMenuContainer,
  Container,
  DashboardHeader,
  HeaderLine,
  Metadata,
  MetadataItem,
  MetadataLocationItem,
  MetadataSiteItem,
  NavigationContainer,
  NavigationContent,
  NavigationRow,
  NavigationRowTitle,
  NavigationTitle,
  SectionContainer,
  TitleStyled,
  ValuePropositionContainer
} from './PrimaryDashboardContent.styles';
import { PrimaryDashboardContentProps } from './PrimaryDashboardContent.types';
import ProductIndicator from './ProductIndicator/ProductIndicator';
import { ProductRowItem } from './ProductRowItem';
import { useDashboardTelemetry } from '@apps/admin/routes/Dashboard/Dashboard.telemetry';
import { DashboardSkeleton, HeaderSkeleton } from '@apps/admin/routes/Dashboard/components/DashboardContentV2/PrimaryDashboardContent/DashboardSkeleton/DashboardSkeleton';
import { useTranslation } from '@shared/core';
import { PageSettingsDialogProvider } from '@apps/registry/admin/routes/YourRegistry/components/PageSettingsDialog/PageSettingsDialog.provider';
import { ValueProposition } from '@apps/admin/routes/Dashboard/components/DashboardContentV2/ValueProposition/ValueProposition';
import { RemoteFeatureFlagKeys, useFeatureValue } from '@shared/core/featureFlags';

export const PrimaryDashboardContent: React.FC<PrimaryDashboardContentProps> = ({
  eventId,
  eventDisplayName,
  eventSettingsUrl,
  eventLocation,
  eventTimestamp,
  productRows,
  adminMenuProps,
  handleShareDialogOpen,
  handleProductSelectionToggleClick,
  handleValuePropositionClick,
  isBottomDrawerVisible
}) => {
  const telemetry = useDashboardTelemetry();
  const { t2 } = useTranslation('adminDashboardV2');
  const { shareEvent } = t2('text');
  const eventDate = useMemo(() => {
    return eventTimestamp ? format(parseISO(eventTimestamp), 'MMMM dd, yyyy') : undefined;
  }, [eventTimestamp]);

  const handleSiteEventClick = () => {
    telemetry.shareClicked();
    handleShareDialogOpen?.();
  };

  const HOTEL_BLOCK_ENABLED: RemoteFeatureFlagKeys = 'hotelBlockEnabled';
  const isHotelBlockEnabled = useFeatureValue(HOTEL_BLOCK_ENABLED);

  return (
    <Container>
      {(eventDisplayName && adminMenuProps && (
        <DashboardHeader>
          <TitleStyled>
            <HeaderLine>{eventDisplayName}</HeaderLine>
          </TitleStyled>
          <AdminMenuContainer>
            <AdminMenu {...adminMenuProps} />
          </AdminMenuContainer>
        </DashboardHeader>
      )) || <HeaderSkeleton />}
      {adminMenuProps.loading || isHotelBlockEnabled.loading ? (
        <DashboardSkeleton />
      ) : (
        <>
          <Metadata>
            {eventDate && (
              <MetadataItem href={eventSettingsUrl} onClick={() => telemetry.openPage({ label: 'eventDate', section: 'EventSettings' })}>
                <Calendar /> <span>{eventDate}</span>
              </MetadataItem>
            )}

            {eventLocation && (
              <MetadataLocationItem
                href={eventSettingsUrl}
                onClick={() =>
                  telemetry.openPage({
                    label: 'eventLocation',
                    section: 'EventSettings'
                  })
                }
              >
                <MapMarker />
                {eventLocation}
              </MetadataLocationItem>
            )}
            <MetadataSiteItem title={shareEvent} onClick={() => handleSiteEventClick()}>
              <IconLeave />
            </MetadataSiteItem>
          </Metadata>
          <NavigationContent>
            <SectionContainer isBottomDrawerVisible={isBottomDrawerVisible}>
              {productRows.map(row => (
                <NavigationRow key={`${row.title}-${row.pageId}`}>
                  <NavigationRowTitle>
                    <NavigationTitle>{row.title}</NavigationTitle>
                    <PageSettingsDialogProvider
                      onDialogOpen={row.onDialogOpen}
                      onDialogClose={row.onDialogClose}
                      onDialogUpdate={row.onDialogUpdate}
                      eventId={eventId}
                      eventPageId={row.pageId}
                      resource={row.title}
                    >
                      <ProductIndicator
                        isEnabled={row.isEnabled}
                        stringOn={row.stringOn}
                        stringOff={row.stringOff}
                        visibility={row.visibility}
                        settingsUrl={row.settingsUrl}
                        showVisibilityDialog={row.showVisibilityDialog}
                        onPageVisibilityClick={row.onPageVisibilityClick}
                        onToggleClick={handleProductSelectionToggleClick}
                      />
                    </PageSettingsDialogProvider>
                  </NavigationRowTitle>
                  {!row.isEnabled && row.valueProposition ? (
                    <ValuePropositionContainer>
                      <ValueProposition
                        title={row.valueProposition.title}
                        description={row.valueProposition.description}
                        ctaText={row.valueProposition.ctaText}
                        path={row.valueProposition.path}
                        onClick={() => {
                          handleValuePropositionClick();
                          if (row.valueProposition?.trackTelemetry) {
                            row.valueProposition?.trackTelemetry();
                          }
                        }}
                        icon={row.valueProposition.icon}
                      />
                    </ValuePropositionContainer>
                  ) : (
                    <NavigationContainer>
                      {row?.children.map(child => (
                        <ProductRowItem key={`${child.title}-${child.path}`} {...child} />
                      ))}
                    </NavigationContainer>
                  )}
                </NavigationRow>
              ))}
            </SectionContainer>
          </NavigationContent>
        </>
      )}
    </Container>
  );
};
